import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { Button } from 'antd';
import { FiCheck } from 'react-icons/fi';
import { LuChevronsDown } from 'react-icons/lu';
import Slider from 'react-slick';

import { CarouselItem, NumberIndex } from '../singleImageSlider/SliderImage';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// Custom (tier 3): antd has no thumbnail strip; reuses the project carousel's slick slider and item.
// Sits where the project carousel does: above the bottom toolbar, over the map.
const Container = styled.div`
	position: absolute;
	z-index: 1;
	bottom: ${({ theme }) => theme.token.controlHeightLG}px;
	left: 0;
	width: 100%;
	padding-top: ${({ theme }) => theme.token.paddingXXS}px;
	background: ${({ $open, theme }) => ($open ? theme.token.colorBgContainer : 'transparent')};
	transition: 0.2s ease-in-out;
	translate: 0
		${({ $open, theme }) => ($open ? '0' : `calc(100% - ${theme.token.controlHeightLG}px)`)};
	pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
`;

const CollapseRow = styled.div`
	display: flex;
	justify-content: center;
	pointer-events: auto;
`;

const Items = styled.div`
	padding: 0 ${({ theme }) => theme.token.paddingXL}px ${({ theme }) => theme.token.paddingSM}px;
	opacity: ${({ $open }) => ($open ? 1 : 0)};
`;

// The slide is wider than its thumbnail, so the saved mark is placed against this
// wrapper (the image's own box), not the slide: it then sits on the image's corner.
const Thumb = styled.span`
	position: relative;
	display: inline-block;
	line-height: 0;
`;

const SavedMark = styled.span`
	position: absolute;
	top: ${({ theme }) => theme.token.marginXXS}px;
	right: ${({ theme }) => theme.token.marginXXS}px;
	z-index: 1;
	display: grid;
	place-items: center;
	width: ${({ theme }) => theme.token.sizeMS}px;
	height: ${({ theme }) => theme.token.sizeMS}px;
	border-radius: 50%;
	background: ${({ theme }) => theme.token.colorSuccess};
	color: ${({ theme }) => theme.token.colorWhite};

	svg {
		width: 70%;
		height: 70%;
	}
`;

/**
 * The images of an object annotation session (single image), in session order.
 * The one being annotated is highlighted and kept in view; the ones already
 * saved for the active object carry a check. Picking one hands its index to
 * `onSelect`.
 *
 * Portals into the map's root so it shares the map's positioning context.
 *
 * @param {{ name: string, thumbnail?: string }[]} props.images
 * @param {number} props.activeIndex
 * @param {Record<string, unknown>} [props.savedTiles] - keyed by image name; a key means saved
 * @param {boolean} [props.disabled] - picking is off while something loads or saves
 * @param {(index: number) => void} props.onSelect
 */
const AnnotationImageCarousel = ({
	images,
	activeIndex,
	savedTiles = {},
	disabled = false,
	onSelect,
}) => {
	const sliderRef = useRef(null);
	const [open, setOpen] = useState(true);

	useEffect(() => {
		sliderRef.current?.slickGoTo?.(activeIndex);
	}, [activeIndex]);

	if (!images.length) return null;

	// Slides to show can not exceed the item count (a slick quirk).
	const count = images.length;
	const settings = {
		infinite: false,
		// As the project carousel: thumbnails load as they come into view, not all at once.
		lazyLoad: 'ondemand',
		arrows: !disabled,
		speed: 300,
		slidesToShow: Math.min(10, count),
		slidesToScroll: Math.min(10, count),
		responsive: [
			{
				breakpoint: 1000,
				settings: {
					slidesToShow: Math.min(5, count),
					slidesToScroll: Math.min(5, count),
				},
			},
		],
	};

	const carousel = (
		<Container $open={open} role="region" aria-label="Images to annotate">
			<CollapseRow>
				<Button
					size="small"
					aria-expanded={open}
					aria-label={open ? 'Hide image carousel' : 'Show image carousel'}
					icon={<LuChevronsDown style={{ rotate: open ? '0deg' : '180deg' }} />}
					onClick={() => setOpen(value => !value)}
				/>
			</CollapseRow>
			<Items $open={open} inert={!open}>
				<Slider
					ref={slider => {
						sliderRef.current = slider;
					}}
					{...settings}>
					{images.map((image, index) => (
						<CarouselItem
							key={image.name}
							type="button"
							title={image.name}
							onClick={() => !disabled && onSelect(index)}
							disabled={disabled}
							$disabled={disabled}
							$active={index === activeIndex}
							aria-current={index === activeIndex ? 'true' : undefined}>
							<Thumb>
								<img src={image.thumbnail} alt={image.name} />
								{savedTiles[image.name] !== undefined && (
									<SavedMark title="Saved" role="img" aria-label="Saved">
										<FiCheck />
									</SavedMark>
								)}
							</Thumb>
							<NumberIndex>{index + 1}</NumberIndex>
						</CarouselItem>
					))}
				</Slider>
			</Items>
		</Container>
	);

	const portalRoot = document.getElementById('portal-root');
	return portalRoot ? createPortal(carousel, portalRoot) : carousel;
};

export default AnnotationImageCarousel;
